import React, { useContext, CSSProperties } from 'react';
import { Card, Col as AntdCol, Row as AntdRow, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TitleRow } from 'components/styledComponents';
import { HomeContext } from 'contexts/home/context';
import { isLocaleEnglish } from 'utils/languageUtils';
import DefaultImage from 'assets/images/home/default-image.svg';
import KnowMoreArrowIcon from 'assets/images/home/know-more-arrow.svg';
import { StyledTitle } from 'components/StyledTitle';
import { Spinner } from 'components';
import { NewsProps } from 'types/home';
import { formatNews } from 'utils/home';
import { useKeycloakReady } from 'hooks';

const { Meta } = Card;
const { Link, Text } = Typography;
const { useBreakpoint } = Grid;

const CardRow = styled(AntdRow)`
  margin-bottom: 1rem;
`;

const CardMeta = styled(Meta)`
  justify-content: space-between;
  flex: 1;
`;

const StyledNewsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  .ant-card-cover img {
    aspect-ratio: 16/10;
  }
  .ant-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    .ant-card-meta {
      justify-content: flex-start;
      .ant-card-meta-detail .ant-card-meta-description {
        line-height: 18px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`;

const KnowMoreLink = styled(Link)`
  display: flex;
  margin-top: 8px;
`;

const KnowMoreLinkText = styled(Text)`
  font-weight: 700;
  color: var(--home-news-promotion-link-color);
  white-space: nowrap;
`;

const KnowMoreLinkImage = styled.img`
  margin-left: 13px;
`;

const NewsCard = ({ title, description, imgSrc, url }: NewsProps) => {
  const { t } = useTranslation();
  return (
    <StyledNewsCard cover={<img alt={`${title}-image`} src={imgSrc ? imgSrc : DefaultImage} />}>
      <CardMeta title={title} description={description} />
      <KnowMoreLink href={url ? url : '#'} target="_blank">
        <KnowMoreLinkText>{t('home.reminderMsgButton')}</KnowMoreLinkText>
        <KnowMoreLinkImage src={KnowMoreArrowIcon} alt="know-more-arrow" />
      </KnowMoreLink>
    </StyledNewsCard>
  );
};

function NewsSection() {
  const { t, i18n } = useTranslation();
  const isEnglish = isLocaleEnglish(i18n.language);

  const { token } = useKeycloakReady();
  const { state } = useContext(HomeContext);
  const news = state.userAccess?.news;
  const formattedNews = formatNews(news, isEnglish, token ?? '');
  // Debugging log for AIOBRB-2121 - will remove after fixed
  console.log({ news, formattedNews });

  const { lg, sm, xs } = useBreakpoint();

  const getColumnStyleByBreakpoint = (): CSSProperties => {
    const COL_WIDTH_LG = 20;
    const COL_WIDTH_SM = 33;
    const COL_WIDTH_XS = 100;

    let columnWidth;

    if (lg) columnWidth = COL_WIDTH_LG;
    else if (sm) columnWidth = COL_WIDTH_SM;
    else if (xs) columnWidth = COL_WIDTH_XS;

    return {
      flexBasis: `${columnWidth}%`,
      width: `${columnWidth}%`,
    };
  };

  return (
    <>
      <TitleRow>
        <AntdCol>
          <StyledTitle color="var(--text-color-secondary)" level={4}>
            {t('home.newsAndPromotions')}
          </StyledTitle>
        </AntdCol>
      </TitleRow>
      {!formattedNews && <Spinner />}
      {formattedNews && formattedNews.length === 0 && (
        <div data-testid="mock-news-not-found-text">{t('home.noNewsAndPromotionsFound')}</div>
      )}
      {formattedNews && formattedNews.length > 0 && (
        <CardRow gutter={[{ xs: 8, sm: 16, md: 24 }, 16]} justify={lg ? 'space-between' : 'start'}>
          {formattedNews.map((news) => (
            <AntdCol key={`${news.title} - ${news.imgSrc}`} style={getColumnStyleByBreakpoint()}>
              <NewsCard {...news} />
            </AntdCol>
          ))}
        </CardRow>
      )}
    </>
  );
}

export default NewsSection;
